/*
 * The brochure as a book (sections/brochure-viewer.liquid).
 *
 * PDF.js, loaded on this page only, draws each PDF page once onto a canvas,
 * kept as a JPEG blob so the same picture can fill a page, either face of a
 * turning leaf and the zoomed view, as CSS backgrounds. A PDF page wider than it
 * is tall is a two-page spread: a desktop shows it whole and turns it as a
 * leaf; a phone splits it into its two pages and you swipe.
 *
 * Only the pages being looked at are fetched. Shopify's file CDN answers range
 * requests (206) but sends no Accept-Ranges header, and PDF.js will not make a
 * range request without one — it would download the whole file first. So the
 * ranges are fetched here and handed over through a PDFDataRangeTransport.
 *
 * Page sizes are read from the first, second and last PDF pages only, and the
 * pages in between are assumed to match the second: a print export is a cover,
 * spreads and a back cover, or all single pages. Reading every page's size up
 * front would cost a request per page before anything could be shown.
 */
if (!customElements.get("brochure-viewer")) {
  const PDFJS = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/";
  const CHUNK = 131072;
  // Pixels per PDF point. The photographs in the export run out of detail at
  // about 2.1, so beyond 3 only the type gets sharper.
  const MAX_SCALE = 3;
  const TURN_MS = 700;
  // How much larger zoom shows the pages, inside the book's own frame.
  const ZOOM = 1.75;
  const phone = window.matchMedia("(max-width: 749px)");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  let pdfjs;
  const loadPdfjs = () =>
    (pdfjs ||= import(PDFJS + "pdf.min.mjs").then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = PDFJS + "pdf.worker.min.mjs";
      return lib;
    }));

  async function openPdf(lib, url) {
    const options = { isEvalSupported: false, rangeChunkSize: CHUNK, disableAutoFetch: true };
    const first = await fetch(url, { headers: { Range: `bytes=0-${CHUNK - 1}` } });
    if (!first.ok) throw new Error(`Brochure PDF: HTTP ${first.status}`);
    const head = new Uint8Array(await first.arrayBuffer());
    const total = Number((first.headers.get("Content-Range") || "").split("/")[1]);
    if (first.status !== 206 || !total) return lib.getDocument({ ...options, data: head }).promise;

    const transport = new lib.PDFDataRangeTransport(total, head);
    transport.requestDataRange = (begin, end) => {
      fetch(url, { headers: { Range: `bytes=${begin}-${end - 1}` } })
        .then((response) => response.arrayBuffer())
        .then((buffer) => transport.onDataRange(begin, new Uint8Array(buffer)))
        .catch((e) => console.error(e));
    };
    return lib.getDocument({ ...options, range: transport }).promise;
  }

  const isSpread = (size) => size.w > size.h * 1.2;

  // The page last looked at, per brochure, for this tab. The theme editor
  // redraws the whole section every time a setting or a product changes, and
  // without this every redraw sent the book back to its cover. A link with
  // #page= still wins. sessionStorage can be blocked, hence the Map as well.
  const seen = new Map();
  const recallPage = (pdf) => {
    if (seen.has(pdf)) return seen.get(pdf);
    try {
      return Number(sessionStorage.getItem(`brochure-page:${pdf}`)) || 0;
    } catch (e) {
      return 0;
    }
  };
  const rememberPage = (pdf, page) => {
    seen.set(pdf, page);
    try {
      sessionStorage.setItem(`brochure-page:${pdf}`, String(page));
    } catch (e) {}
  };

  function publishAnalytics(name, payload) {
    try {
      window.Shopify?.analytics?.publish?.(name, payload);
    } catch (e) {
      /* analytics must never break the storefront */
    }
  }

  customElements.define(
    "brochure-viewer",
    class BrochureViewer extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector("[data-bv-stage]");
        this.view = this.querySelector("[data-bv-view]");
        this.status = this.querySelector("[data-bv-status]");
        this.counter = this.querySelector("[data-bv-counter]");
        this.prevButton = this.querySelector("[data-bv-prev]");
        this.nextButton = this.querySelector("[data-bv-next]");
        this.zoomButton = this.querySelector("[data-bv-zoom]");
        this.fullButton = this.querySelector("[data-bv-fullscreen]");
        this.zoomLayer = this.querySelector("[data-bv-zoom-layer]");
        this.zoomPages = this.querySelector("[data-bv-zoom-pages]");
        this.shop = this.querySelector("[data-bv-shop]");

        this.images = new Map(); // PDF page number -> { scale, url }
        this.drawing = new Map(); // PDF page number -> { scale, promise }
        this.queue = Promise.resolve();
        this.at = 0; // index of the first page in view
        this.mode = null;

        this.prevButton.addEventListener("click", () => this.go(-1));
        this.nextButton.addEventListener("click", () => this.go(1));
        this.zoomButton.addEventListener("click", () => (this.zoomed ? this.closeZoom() : this.openZoom()));
        this.addEventListener("click", (evt) => {
          const button = evt.target.closest(".js-brochure-add");
          if (button) this.add(button);
        });

        if (document.fullscreenEnabled) {
          this.fullButton.hidden = false;
          this.fullButton.addEventListener("click", () => {
            if (document.fullscreenElement) document.exitFullscreen();
            else this.stage.requestFullscreen().catch(() => {});
          });
          this.querySelector("[data-bv-exit]").addEventListener("click", () => document.exitFullscreen());
          this.stage.addEventListener("fullscreenchange", () => {
            this.fullButton.setAttribute("aria-label", document.fullscreenElement ? "Exit full screen" : "Full screen");
            // Keep the zoom, and the spot it is on, through the change of size.
            if (this.zoomed) requestAnimationFrame(() => this.placeZoom());
          });
        }

        this.fit();
        this.onKey = (evt) => this.key(evt);
        document.addEventListener("keydown", this.onKey);

        // In the theme editor, picking a "Products on a page" block turns the
        // book to that page, so you see the page you are filling.
        this.onBlockSelect = (evt) => {
          const list = evt.target.closest?.("[data-bv-page]");
          if (!list || !this.contains(list)) return;
          if (!this.leaves) {
            this.wantPage = list.dataset.bvPage;
            return;
          }
          const page = this.pageNumber(list.dataset.bvPage);
          if (page) this.show(page - 1);
        };
        document.addEventListener("shopify:block:select", this.onBlockSelect);
        this.bindZoomPan();

        this.start().catch((e) => this.fail(e));
      }

      disconnectedCallback() {
        document.removeEventListener("keydown", this.onKey);
        document.removeEventListener("shopify:block:select", this.onBlockSelect);
        this.resizeObserver?.disconnect();
        this.pdf?.destroy();
        this.images.forEach((image) => URL.revokeObjectURL(image.url));
      }

      async start() {
        const lib = await loadPdfjs();
        this.pdf = await openPdf(lib, this.dataset.pdf);
        const n = this.pdf.numPages;
        const sizeOf = (i) =>
          this.pdf.getPage(i).then((page) => {
            const viewport = page.getViewport({ scale: 1 });
            return { w: viewport.width, h: viewport.height };
          });
        const [first, middle, last] = await Promise.all([sizeOf(1), sizeOf(Math.min(2, n)), sizeOf(n)]);
        this.sizes = (i) => (i === 1 ? first : i === n ? last : middle);
        this.buildModel(n);

        const asked = /page=(\d+)/.exec(location.hash);
        const page = this.wantPage ? this.pageNumber(this.wantPage) : asked ? Number(asked[1]) : recallPage(this.dataset.pdf);
        if (page) this.at = Math.min(Math.max(page - 1, 0), this.leaves.length - 1);

        this.layout();
        this.status.hidden = true;
        this.zoomButton.disabled = false;

        this.resizeObserver = new ResizeObserver(() => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => this.resized(), 150);
        });
        this.resizeObserver.observe(this.view);
      }

      fail(error) {
        console.error(error);
        this.status.hidden = false;
        this.status.classList.add("brochure__status--error");
        this.status.innerHTML = "";
        const message = document.createElement("p");
        message.append("Sorry, the brochure hasn't loaded. ");
        const link = document.createElement("a");
        link.href = this.dataset.pdf;
        link.textContent = "Open the PDF instead";
        message.append(link);
        this.status.append(message);
      }

      // leaves: every page of the book, in order — { pdf, part: full|left|right }.
      // spreads: what a desktop shows at once — { left, right } as leaf indexes,
      // null for the empty side beside the front and back covers.
      buildModel(n) {
        this.leaves = [];
        this.spreads = [];
        const add = (pdf, part) => this.leaves.push({ pdf, part }) - 1;
        const anySpread = [1, 2, n].some((i) => isSpread(this.sizes(i)));

        if (anySpread) {
          for (let i = 1; i <= n; i++) {
            if (isSpread(this.sizes(i))) {
              this.spreads.push({ left: add(i, "left"), right: add(i, "right") });
            } else {
              const leaf = add(i, "full");
              this.spreads.push(i === n && n > 1 ? { left: leaf, right: null } : { left: null, right: leaf });
            }
          }
        } else {
          // Single pages, paired like a printed book: the cover alone on the right.
          for (let i = 1; i <= n; i++) add(i, "full");
          this.spreads.push({ left: null, right: 0 });
          for (let k = 1; k < n; k += 2) this.spreads.push({ left: k, right: k + 1 < n ? k + 1 : null });
        }

        this.spreadOf = [];
        this.spreads.forEach((spread, s) => {
          if (spread.left !== null) this.spreadOf[spread.left] = s;
          if (spread.right !== null) this.spreadOf[spread.right] = s;
        });

        const page = this.leafSize(0);
        this.style.setProperty("--bv-page-ratio", (page.w / page.h).toFixed(4));
        this.style.setProperty("--bv-ratio", ((page.w * 2) / page.h).toFixed(4));
      }

      leafSize(index) {
        const leaf = this.leaves[index];
        const size = this.sizes(leaf.pdf);
        return leaf.part === "full" ? size : { w: size.w / 2, h: size.h };
      }

      spreadLeaves(s) {
        const spread = this.spreads[s];
        return [spread.left, spread.right].filter((leaf) => leaf !== null);
      }

      // ------------------------------------------------------------- layout

      layout() {
        this.mode = phone.matches ? "phone" : "book";
        this.turning?.finish();
        this.view.replaceChildren();
        if (this.mode === "book") this.buildBook();
        else this.buildTrack();
        this.update();
      }

      // The book is sized by the screen's height as well as its width, so the
      // whole spread and its arrows fit without scrolling. --bv-chrome is the
      // height everything else takes, measured: the header's height changes
      // with the breakpoint. A phone keeps the CSS figure, which lets the page
      // fill the width instead.
      fit() {
        if (phone.matches || document.fullscreenElement) {
          if (phone.matches) this.style.removeProperty("--bv-chrome");
          return;
        }
        const bar = this.querySelector("[data-bv-tools]");
        const top = this.stage.getBoundingClientRect().top + window.scrollY;
        const below = bar.offsetHeight + parseFloat(getComputedStyle(this.stage).rowGap) + 16;
        this.style.setProperty("--bv-chrome", `${Math.ceil(top + below)}px`);
      }

      resized() {
        this.fit();
        if ((phone.matches ? "phone" : "book") !== this.mode) {
          this.layout();
          return;
        }
        if (this.mode === "phone") this.track.scrollLeft = this.at * this.stride();
        this.want();
        if (this.zoomed) this.placeZoom();
      }

      face(className) {
        const el = document.createElement("div");
        el.className = `brochure__page ${className}`;
        el.setAttribute("role", "img");
        return el;
      }

      buildBook() {
        this.book = document.createElement("div");
        this.book.className = "brochure__book";
        this.leftFace = this.face("brochure__page--left");
        this.rightFace = this.face("brochure__page--right");
        this.leaf = document.createElement("div");
        this.leaf.className = "brochure__leaf";
        this.leaf.hidden = true;
        this.leaf.setAttribute("aria-hidden", "true");
        this.leafFront = this.face("brochure__leaf-front");
        this.leafBack = this.face("brochure__leaf-back");
        this.leaf.append(this.leafFront, this.leafBack);
        this.book.append(this.leftFace, this.rightFace, this.leaf);
        this.view.append(this.book);

        this.spread = this.spreadOf[this.at];
        this.at = this.spreadLeaves(this.spread)[0];
        this.showSpread(this.spread);

        this.leftFace.addEventListener("click", () => this.go(-1));
        this.rightFace.addEventListener("click", () => this.go(1));

        // A tablet in landscape gets the book too, so it swipes as well as taps.
        let startX = null;
        let startY = 0;
        this.book.addEventListener("pointerdown", (evt) => {
          if (evt.pointerType === "mouse") return;
          startX = evt.clientX;
          startY = evt.clientY;
        });
        this.book.addEventListener("pointerup", (evt) => {
          if (startX === null) return;
          const dx = evt.clientX - startX;
          const dy = evt.clientY - startY;
          startX = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            this.swiped = true;
            this.go(dx < 0 ? 1 : -1);
          }
        });
        this.book.addEventListener(
          "click",
          (evt) => {
            if (!this.swiped) return;
            this.swiped = false;
            evt.stopPropagation();
          },
          true
        );
      }

      showSpread(s) {
        const spread = this.spreads[s];
        this.paint(this.leftFace, spread.left);
        this.paint(this.rightFace, spread.right);
        this.book.dataset.alone = spread.left === null ? "right" : spread.right === null ? "left" : "";
      }

      buildTrack() {
        this.track = document.createElement("div");
        this.track.className = "brochure__track";
        this.leaves.forEach((leaf, i) => {
          const slide = document.createElement("div");
          slide.className = "brochure__slide";
          const face = this.face("");
          slide.append(face);
          this.track.append(slide);
          this.paint(face, i);
        });
        this.view.append(this.track);
        this.track.scrollLeft = this.at * this.stride();
        this.track.addEventListener(
          "scroll",
          () => {
            cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = requestAnimationFrame(() => {
              const at = Math.round(this.track.scrollLeft / this.stride());
              if (at === this.at || at < 0 || at >= this.leaves.length) return;
              this.at = at;
              this.update();
            });
          },
          { passive: true }
        );
      }

      stride() {
        const slides = this.track.children;
        return slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : this.track.clientWidth;
      }

      // --------------------------------------------------------- navigation

      go(step) {
        this.closeZoom({ focus: false, instant: true });
        if (this.mode === "book") {
          this.flip(this.spread + step);
          return;
        }
        const to = this.at + step;
        if (to < 0 || to >= this.leaves.length) return;
        this.track.scrollTo({ left: to * this.stride(), behavior: reducedMotion.matches ? "auto" : "smooth" });
      }

      // Straight to a page: turns to its spread on a desktop, swipes to it on a phone.
      show(leaf) {
        if (leaf < 0 || leaf >= this.leaves.length) return;
        if (this.mode === "book") this.flip(this.spreadOf[leaf]);
        else this.track.scrollTo({ left: leaf * this.stride(), behavior: reducedMotion.matches ? "auto" : "smooth" });
      }

      flip(to) {
        const from = this.spread;
        if (to < 0 || to >= this.spreads.length || to === from) return;
        this.turning?.finish();

        const a = this.spreads[from];
        const b = this.spreads[to];
        this.spread = to;
        this.at = this.spreadLeaves(to)[0];
        this.update();

        if (reducedMotion.matches) {
          this.showSpread(to);
          return;
        }

        // The leaf lies over the page being turned. Its back is the page the turn
        // brings into view, and the page under it is the one it uncovers.
        const forward = to > from;
        if (forward) {
          this.paint(this.leafFront, a.right);
          this.paint(this.leafBack, b.left);
          this.paint(this.rightFace, b.right);
        } else {
          this.paint(this.leafFront, a.left);
          this.paint(this.leafBack, b.right);
          this.paint(this.leftFace, b.left);
        }
        delete this.leaf.dataset.dir;
        void this.leaf.offsetWidth; // restart the animation if the last turn just ended
        this.leaf.hidden = false;
        this.leaf.dataset.dir = forward ? "next" : "prev";
        this.book.dataset.from = a.left === null ? "right" : a.right === null ? "left" : "";
        // Over an empty half (beside a cover) no page at rest can cast the shadow,
        // so that face of the leaf casts it: the one the page had, or will have.
        this.leafFront.classList.toggle("is-casting", (forward ? b.right : b.left) === null);
        this.leafBack.classList.toggle("is-casting", (forward ? a.left : a.right) === null);
        // The covers sit centred, so the book slides across as it opens or closes.
        this.book.dataset.alone = b.left === null ? "right" : b.right === null ? "left" : "";

        const finish = () => {
          clearTimeout(timer);
          this.leaf.removeEventListener("animationend", finish);
          this.leaf.hidden = true;
          delete this.leaf.dataset.dir;
          delete this.book.dataset.from;
          this.showSpread(to);
          this.turning = null;
        };
        const timer = setTimeout(finish, TURN_MS + 150);
        this.leaf.addEventListener("animationend", finish);
        this.turning = { finish };
      }

      key(evt) {
        if (evt.altKey || evt.ctrlKey || evt.metaKey || evt.shiftKey || !this.leaves) return;
        if (evt.target.closest?.("input, textarea, select, [contenteditable]")) return;
        if (evt.key === "Escape" && this.zoomed) {
          this.closeZoom();
          return;
        }
        if (evt.key !== "ArrowLeft" && evt.key !== "ArrowRight") return;
        const box = this.view.getBoundingClientRect();
        if (box.bottom < 0 || box.top > window.innerHeight) return;
        evt.preventDefault();
        this.go(evt.key === "ArrowRight" ? 1 : -1);
      }

      update() {
        const inView = this.mode === "book" ? this.spreadLeaves(this.spread) : [this.at];
        const pages = inView.map((leaf) => leaf + 1).join("–");
        this.counter.textContent = `${pages} / ${this.leaves.length}`;
        this.prevButton.disabled = this.at === 0;
        this.nextButton.disabled = inView[inView.length - 1] === this.leaves.length - 1;

        const url = location.pathname + location.search + (this.at > 0 ? `#page=${this.at + 1}` : "");
        history.replaceState(history.state, "", url);
        rememberPage(this.dataset.pdf, this.at + 1);

        this.updateShop();
        this.want();

        clearTimeout(this.viewTimer);
        this.viewTimer = setTimeout(
          () => publishAnalytics("swd_brochure_page_viewed", { brochure: this.dataset.title, page: this.at + 1, pages }),
          1500
        );
      }

      // ----------------------------------------------------------- drawing

      // The on-screen width of one page of the book.
      pageWidth() {
        if (this.mode === "book") return this.book.clientWidth / 2;
        return this.track.querySelector(".brochure__page").clientWidth;
      }

      // Draw what is in view first, then a spread either side so a turn or a
      // swipe finds its pages ready.
      want() {
        const width = this.pageWidth();
        const s = this.spreadOf[this.at];
        const order = [s, s + 1, s - 1, s + 2];
        const pdfPages = [];
        order.forEach((spread) => {
          if (spread < 0 || spread >= this.spreads.length) return;
          this.spreadLeaves(spread).forEach((leaf) => {
            const pdf = this.leaves[leaf].pdf;
            if (!pdfPages.includes(pdf)) pdfPages.push(pdf);
          });
        });
        pdfPages.forEach((pdf) => this.draw(pdf, width * (isSpread(this.sizes(pdf)) ? 2 : 1)));
      }

      draw(pdf, cssWidth) {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const scale = Math.min(MAX_SCALE, (cssWidth * dpr) / this.sizes(pdf).w);
        const have = this.images.get(pdf);
        if (have && have.scale >= scale * 0.9) return Promise.resolve(have);
        const pending = this.drawing.get(pdf);
        if (pending && pending.scale >= scale * 0.9) return pending.promise;

        const promise = (this.queue = this.queue
          .catch(() => {})
          .then(() => this.render(pdf, scale))
          .then((image) => {
            const old = this.images.get(pdf);
            if (!old || old.scale < image.scale) this.images.set(pdf, image);
            this.repaint(pdf);
            return image;
          })
          .finally(() => {
            if (this.drawing.get(pdf)?.promise === promise) this.drawing.delete(pdf);
          }));
        this.drawing.set(pdf, { scale, promise });
        return promise;
      }

      async render(pdf, scale) {
        const page = await this.pdf.getPage(pdf);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        await page.render({ canvasContext: canvas.getContext("2d", { alpha: false }), viewport }).promise;
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
        canvas.width = 0;
        canvas.height = 0;
        const url = URL.createObjectURL(blob);
        // Decoded before it is used, so a turning leaf never shows a blank back.
        const img = new Image();
        img.src = url;
        await img.decode().catch(() => {});
        return { scale, url };
      }

      paint(el, leafIndex) {
        if (leafIndex === null || leafIndex === undefined) {
          el.dataset.leaf = "";
          el.classList.add("is-blank");
          el.style.backgroundImage = "";
          el.removeAttribute("aria-label");
          return;
        }
        const leaf = this.leaves[leafIndex];
        const image = this.images.get(leaf.pdf);
        el.dataset.leaf = leafIndex;
        el.dataset.part = leaf.part;
        el.setAttribute("aria-label", `Page ${leafIndex + 1}`);
        el.classList.remove("is-blank");
        el.classList.toggle("is-loading", !image);
        el.style.backgroundImage = image ? `url("${image.url}")` : "";
      }

      repaint(pdf) {
        this.querySelectorAll(".brochure__page[data-leaf]").forEach((el) => {
          const index = el.dataset.leaf;
          if (index !== "" && this.leaves[index].pdf === pdf) this.paint(el, Number(index));
        });
      }

      // -------------------------------------------------------------- zoom

      // Zoom stays inside the book: a window the size and place of the pages in
      // view, holding the same pages, which grow to ZOOM times and follow the
      // pointer — a mouse by hovering, a finger by dragging; a click or a tap
      // zooms back out. Everything eases toward its target a little each frame,
      // so zooming in, panning and zooming out are one smooth motion. The pages
      // are resized rather than scaled, so the browser draws them sharp.
      openZoom() {
        this.turning?.finish();
        const inView = this.mode === "book" ? this.spreadLeaves(this.spread) : [this.at];
        this.zoomPages.replaceChildren(
          ...inView.map((leaf) => {
            const el = this.face("");
            this.paint(el, leaf);
            return el;
          })
        );
        this.zoomed = true;
        this.zoomAnchor = null;
        this.zoomNow = { s: 1, x: 0.5, y: 0.5 };
        this.zoomTarget = { s: ZOOM, x: 0.5, y: 0.5 };
        this.zoomLayer.hidden = false;
        this.zoomButton.setAttribute("aria-pressed", "true");
        this.zoomButton.setAttribute("aria-label", "Zoom out");
        this.placeZoom();
        this.animateZoom();
      }

      placeZoom() {
        const faces =
          this.mode === "book"
            ? [this.leftFace, this.rightFace].filter((el) => !el.classList.contains("is-blank"))
            : [this.track.children[this.at].firstElementChild];
        const boxes = faces.map((el) => el.getBoundingClientRect());
        const stage = this.stage.getBoundingClientRect();
        const left = Math.min(...boxes.map((r) => r.left));
        const top = Math.min(...boxes.map((r) => r.top));
        const width = Math.max(...boxes.map((r) => r.right)) - left;
        const height = Math.max(...boxes.map((r) => r.bottom)) - top;
        Object.assign(this.zoomLayer.style, {
          left: `${left - stage.left}px`,
          top: `${top - stage.top}px`,
          width: `${width}px`,
          height: `${height}px`,
        });
        this.applyZoom();
        const pageWidth = (width * ZOOM) / this.zoomPages.children.length;
        [...this.zoomPages.children].forEach((el) => {
          const leaf = this.leaves[el.dataset.leaf];
          this.draw(leaf.pdf, pageWidth * (leaf.part === "full" ? 1 : 2));
        });
      }

      applyZoom() {
        const { s, x, y } = this.zoomNow;
        const w = this.zoomLayer.clientWidth;
        const h = this.zoomLayer.clientHeight;
        this.zoomPages.style.width = `${w * s}px`;
        this.zoomPages.style.transform = `translate(${-(s - 1) * w * x}px, ${-(s - 1) * h * y}px)`;
      }

      animateZoom() {
        if (this.zoomFrame) return;
        const step = () => {
          const now = this.zoomNow;
          const target = this.zoomTarget;
          const ease = reducedMotion.matches ? 1 : 0.12;
          let settled = true;
          ["s", "x", "y"].forEach((key) => {
            now[key] += (target[key] - now[key]) * ease;
            if (Math.abs(target[key] - now[key]) > 0.0015) settled = false;
            else now[key] = target[key];
          });
          this.applyZoom();
          this.zoomFrame = settled ? null : requestAnimationFrame(step);
          if (settled && !this.zoomed) this.hideZoom();
        };
        this.zoomFrame = requestAnimationFrame(step);
      }

      // instant: a page turn, which should not wait for the zoom to ease out.
      closeZoom({ focus = true, instant = false } = {}) {
        if (!this.zoomed) return;
        this.zoomed = false;
        this.zoomTarget = { s: 1, x: this.zoomNow.x, y: this.zoomNow.y };
        this.zoomButton.setAttribute("aria-pressed", "false");
        this.zoomButton.setAttribute("aria-label", "Zoom in");
        if (focus) this.zoomButton.focus();
        if (instant) this.hideZoom();
        else this.animateZoom();
      }

      hideZoom() {
        cancelAnimationFrame(this.zoomFrame);
        this.zoomFrame = null;
        this.zoomLayer.hidden = true;
        this.zoomPages.replaceChildren();
        this.zoomPages.style.transform = "";
      }

      bindZoomPan() {
        const clamp = (n) => Math.min(1, Math.max(0, n));
        let touch = null;
        this.zoomLayer.addEventListener("pointermove", (evt) => {
          if (!this.zoomed) return;
          const box = this.zoomLayer.getBoundingClientRect();
          if (evt.pointerType === "mouse") {
            // Where the pointer comes in, the view stays put: the gap between
            // the two is kept and shrinks as the pointer travels, so the view
            // lines up with it again over about two-thirds of the frame and
            // every edge can still be reached. Without it, entering the frame
            // from the zoom button below snapped the view to the bottom corner.
            const fx = clamp((evt.clientX - box.left) / box.width);
            const fy = clamp((evt.clientY - box.top) / box.height);
            const a = this.zoomAnchor;
            if (!a) {
              this.zoomAnchor = { x: this.zoomTarget.x - fx, y: this.zoomTarget.y - fy, fx, fy };
            } else {
              const fade = Math.max(0, 1 - Math.hypot(fx - a.fx, fy - a.fy) * 1.5);
              a.x *= fade;
              a.y *= fade;
              a.fx = fx;
              a.fy = fy;
            }
            this.zoomTarget.x = clamp(fx + this.zoomAnchor.x);
            this.zoomTarget.y = clamp(fy + this.zoomAnchor.y);
          } else if (touch) {
            // A finger drags the page: moving it the whole visible width pans
            // as far as the page goes.
            const room = ZOOM - 1;
            this.zoomTarget.x = clamp(touch.x - (evt.clientX - touch.clientX) / (box.width * room));
            this.zoomTarget.y = clamp(touch.y - (evt.clientY - touch.clientY) / (box.height * room));
            if (Math.abs(evt.clientX - touch.clientX) + Math.abs(evt.clientY - touch.clientY) > 8) touch.moved = true;
          }
          this.animateZoom();
        });
        // A mouse click zooms out; a finger only when it taps without dragging.
        let pointer = "mouse";
        this.zoomLayer.addEventListener("pointerdown", (evt) => {
          pointer = evt.pointerType;
          if (pointer === "mouse") return;
          touch = { x: this.zoomTarget.x, y: this.zoomTarget.y, clientX: evt.clientX, clientY: evt.clientY, moved: false };
        });
        this.zoomLayer.addEventListener("pointerup", () => {
          if (touch && !touch.moved) this.closeZoom({ focus: false });
          touch = null;
        });
        this.zoomLayer.addEventListener("pointercancel", () => (touch = null));
        this.zoomLayer.addEventListener("pointerleave", () => (this.zoomAnchor = null));
        this.zoomLayer.addEventListener("click", () => {
          if (pointer === "mouse") this.closeZoom({ focus: false });
        });
      }

      // -------------------------------------------------------------- shop

      // "Page 14" -> 14, "Front cover" -> 1, "Back cover" -> the last page.
      pageNumber(text) {
        const label = String(text || "").toLowerCase();
        if (label.includes("back")) return this.leaves.length;
        const number = /\d+/.exec(label);
        if (number) return Number(number[0]);
        return label.includes("cover") ? 1 : NaN;
      }

      updateShop() {
        if (!this.shop) return;
        const pages = this.spreadLeaves(this.spreadOf[this.at]).map((leaf) => leaf + 1);
        const seen = new Set();
        let shown = 0;
        this.shop.querySelectorAll("[data-bv-page]").forEach((list) => {
          const on = pages.includes(this.pageNumber(list.dataset.bvPage));
          list.hidden = !on;
          if (!on) return;
          list.querySelectorAll("[data-swd-list-item]").forEach((item) => {
            const repeat = seen.has(item.dataset.productId);
            item.hidden = repeat;
            if (repeat) return;
            seen.add(item.dataset.productId);
            item.dataset.index = ++shown;
          });
        });
        this.shop.querySelector("[data-bv-shop-empty]").hidden = shown > 0;
        this.showError(false);
      }

      // Same as "Pairs well with" (assets/pdp-pairs-well.js): post the one
      // variant, then publish cartAdd so the mini-cart opens as it does for Add
      // to Basket. swd-list-tracking.js turns "swd:list-add" into the GA4 event.
      add(button) {
        if (button.getAttribute("aria-disabled") === "true") return;
        button.setAttribute("aria-disabled", "true");
        button.classList.add("loading");
        this.showError(false);

        fetch(`${routes.cart_add_url}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variantId), quantity: 1, properties: { _added_from: "brochure" } }],
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || window.cartStrings?.error);
              return;
            }
            this.announce(button, response.items && response.items[0]);
            publish(PUB_SUB_EVENTS.cartAdd);
          })
          .catch((e) => {
            console.error(e);
            this.showError(window.cartStrings?.error);
          })
          .finally(() => {
            button.classList.remove("loading");
            button.removeAttribute("aria-disabled");
          });
      }

      announce(button, line) {
        try {
          const item = button.closest("[data-swd-list-item]");
          if (!item) return;
          line = line || {};
          const pence = typeof line.final_line_price === "number" ? line.final_line_price : line.original_line_price;
          item.dispatchEvent(
            new CustomEvent("swd:list-add", {
              bubbles: true,
              detail: { quantity: line.quantity || 1, value: typeof pence === "number" ? pence / 100 : undefined },
            })
          );
        } catch (e) {}
      }

      showError(message) {
        const error = this.shop?.querySelector("[data-bv-shop-error]");
        if (!error) return;
        error.textContent = message || "";
        error.hidden = !message;
      }
    }
  );
}
